import { useLocalObservable } from 'mobx-react-lite';
import { RefObject } from 'react';
import { useNavigate } from 'react-router-dom';

import { FileMediumIcon, IcArticleIcon, LocationIcon } from '@gem/icons';
import { ILocation } from '@gem/models/commonModel';
import { useTranslation } from '@gem/modules/i18n';
import { IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { ScheduleSettingsPopup } from 'APP/main/PopupManager/Popups/ScheduleSettingsPopup/ScheduleSettingsPopup';
import { SelectLocationPopup } from 'APP/main/PopupManager/Popups/SelectLocationPopup/SelectLocationPopup';
import { Features } from 'APP/packages/features/Features';
import { buildPath } from 'APP/router/helpers/buildPath';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import { Schedule } from 'STORE/Groups/Schedule';
import { LocationPayload } from 'STORE/Messages/Message/Payload/LocationPayload/LocationPayload';

import FileUploadButton from '../FileUploadButton';

interface IAttachmentsButtonMenuPresenter {
  group: Group | null;
  menuItems: IPopoverMenuItem[];
  openArticle(): void;
  openSelectLocationPopup(): void;
  onSelectLocation(location: ILocation): void;
}

export const useAttachmentsButtonMenuPresenter = (
  fileUploadRef: RefObject<FileUploadButton>
): IAttachmentsButtonMenuPresenter => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const presenter = useLocalObservable<IAttachmentsButtonMenuPresenter>(() => ({
    get group(): Group | null {
      return Entities.GroupsStore.activeGroup;
    },

    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'Article',
          icon: IcArticleIcon,
          title: t('channel_article'),
          onClick: presenter.openArticle,
          isVisible:
            Features.isArticleEnabled() &&
            presenter.group?.isChannel &&
            presenter.group?.withMeInAdmins,
        },
        {
          key: 'File',
          icon: FileMediumIcon,
          title: t('message_desctription_file'),
          onClick: () => fileUploadRef.current && fileUploadRef.current.open(),
          isVisible: true,
        },
        {
          key: 'Location',
          icon: LocationIcon,
          title: t('message_desctription_location'),
          onClick: presenter.openSelectLocationPopup,
          isVisible: Features.isLocationMessagesEnabled(),
        },
      ].filter((item) => item.isVisible);
    },

    openArticle(): void {
      if (!presenter.group) {
        return;
      }
      // TODO: вынести RouterLink в константы, когда включим артикли
      navigate(
        buildPath('/channels/:id/article/:messageId', { id: presenter.group.id, messageId: '' })
      );
    },

    openSelectLocationPopup(): void {
      if (!presenter.group) {
        return;
      }

      Tasks.app.openPopup(SelectLocationPopup, {
        params: {
          groupId: presenter.group.id,
          onSelect: presenter.onSelectLocation,
        },
      });
    },

    onSelectLocation(location: ILocation): void {
      if (!presenter.group) {
        return;
      }

      const messageData = {
        payload: new LocationPayload(location),
        repliedMessage: Entities.InputPanelsStore.activeInputPanel.replyMessage,
        quote: Entities.InputPanelsStore.activeInputPanel.quote,
      };

      const mustBeScheduled = presenter.group instanceof Schedule;
      const canSend = Tasks.messaging.canSendMessage({
        groupId: presenter.group.id,
        isCheckSlowMode: !mustBeScheduled,
        payload: messageData.payload,
      });

      if (!canSend.result) {
        canSend.error && Tasks.app.showToast(canSend.error);
        return;
      }

      if (mustBeScheduled) {
        Tasks.app.openPopup(ScheduleSettingsPopup, {
          params: {
            onSubmit: async (date: Date): Promise<void> => {
              await Tasks.messaging.createScheduledMessage((presenter.group as Schedule).groupId!, {
                ...messageData,
                scheduleTime: date.getTime(),
              });
            },
          },
        });

        return;
      }

      Tasks.messaging.createNewMessages(presenter.group.id, [messageData]);
    },
  }));

  return presenter;
};
